// Ride cashback: settings, finance reports, per-ride reversal. Shapes mirror
// apps/backend/src/services/{cashbackSettings,adminCashback,cashback}.service.
import type { CashbackSettingsDto, CashbackStatus } from "@safiri/shared";
import { api, unwrap, cleanParams } from "../lib/axios";
import type { ApiEnvelope } from "../types/api";
import type { Window } from "./console";

export interface CashbackSettings extends CashbackSettingsDto {
  updatedAt: string | null;
  updatedBy: string | null;
}

export interface CashbackSettingsVersion extends CashbackSettingsDto {
  id: string;
  effectiveFrom: string;
  changedBy: string | null;
  note: string | null;
}

export interface CashbackTotals {
  rides: number;
  passenger: number;
  driver: number;
  driverCommissionOffset: number;
  fareBase: number;
  platformEarning: number;
  reversedRides: number;
  passengerRecovered: number;
  passengerWrittenOff: number;
  driverRecovered: number;
  granted: number;
  net: number;
  shareOfPlatformEarning: number | null;
}

export interface CashbackSummary {
  window: { from: string; to: string; groupBy: Window["groupBy"] };
  previousWindow: { from: string; to: string };
  totals: CashbackTotals;
  previous: CashbackTotals;
  series: (CashbackTotals & { bucket: string })[];
  byRideType: (CashbackTotals & { key: string; name: string })[];
}

export interface CashbackRecord {
  _id: string;
  tripID: string;
  passengerID: string | null;
  driverID: string | null;
  rideTypeName: string | null;
  paymentMode: string | null;
  fareBase: number;
  passengerPercentage: number;
  driverPercentage: number;
  passengerAmount: number;
  driverAmount: number;
  driverCommissionOffset: number;
  driverWithdrawable: number;
  platformEarning: number;
  status: CashbackStatus;
  source: "completion" | "reconcile";
  grantedAt: string;
  tripCompletedAt: string;
  reversal: {
    at: string;
    byName: string | null;
    reason: string;
    passengerRecovered: number;
    passengerWrittenOff: number;
    driverRecovered: number;
  } | null;
  trip?: { passengerName?: string; driverName?: string; distanceKm?: number | null };
}

export interface RepeatPair {
  passengerId: string;
  driverId: string;
  passengerName?: string;
  passengerPhone?: string;
  driverName?: string;
  driverPhone?: string;
  rides: number;
  grantedRides: number;
  distanceKm: number;
  cashbackPassenger: number;
  cashbackDriver: number;
  firstAt: string;
  lastAt: string;
  tripIds: string[];
}

const BASE = "/admin/finance/cashback";

async function get<T>(url: string, params?: Record<string, unknown>): Promise<T> {
  const res = await api.get<ApiEnvelope<T>>(url, { params: params ? cleanParams(params) : undefined });
  return unwrap(res);
}

export const cashbackApi = {
  settings: () => get<CashbackSettings>(`${BASE}/settings`),
  history: () => get<CashbackSettingsVersion[]>(`${BASE}/settings/history`),
  async updateSettings(body: CashbackSettingsDto & { note?: string }): Promise<CashbackSettings> {
    const res = await api.patch<ApiEnvelope<CashbackSettings>>(`${BASE}/settings`, body);
    return unwrap(res);
  },

  summary: (w: Window) => get<CashbackSummary>(`${BASE}/summary`, { ...w }),
  records: (q: { from?: string; to?: string; status?: CashbackStatus; page?: number; limit?: number }) =>
    get<{ items: CashbackRecord[]; total: number; page: number; limit: number }>(`${BASE}/records`, q),
  repeatPairs: (w: Window & { minRides?: number; maxDistanceKm?: number }) =>
    get<{ pairs: RepeatPair[]; minRides: number; maxDistanceKm: number }>(`${BASE}/repeat-pairs`, { ...w }),

  forTrip: (tripId: string) => get<CashbackRecord | null>(`${BASE}/trips/${tripId}`),
  async reverse(tripId: string, reason: string) {
    const res = await api.post<
      ApiEnvelope<{ tripId: string; passengerRecovered: number; passengerWrittenOff: number; driverRecovered: number }>
    >(`${BASE}/trips/${tripId}/reverse`, { reason });
    return unwrap(res);
  },
};
